import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useCompanyDeviceSettings } from '../hooks/useCompanyDeviceSettings';
import { usePasskeyChallenge } from '../hooks/usePasskeyChallenge';
import apiService from '../services/api';
import ToggleSwitch from '../components/ToggleSwitch';
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UserIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CommandLineIcon,
  PlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface Device {
  id: string;
  name: string;
  type: string;
  hardware_id?: string;
  status?: string;
  created_at: string;
  updated_at?: string;
  last_seen?: string;
  system_info?: Record<string, any>;
  metadata?: Record<string, any>;
  user_ids?: string[] | any[];
}

interface DeviceWithUser {
  device: Device;
  userId: string;
  userName?: string;
  userEmail?: string;
}

const Devices: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('devices');
  const createCLITokenChallenge = usePasskeyChallenge('create_cli_token');
  const isSysadmin = user?.role === 'sysadmin';
  const isAdmin = user?.role === 'admin' || user?.role === 'sysadmin';
  const isRegularUser = user?.role === 'user';
  
  usePageTitle(t('title'), t('subtitle'));
  const queryClient = useQueryClient();
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [deletingDeviceId, setDeletingDeviceId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);

  // CLI token state (only used when viewing own devices)
  const [showCreateCLITokenModal, setShowCreateCLITokenModal] = useState(false);
  const [showCLITokenModal, setShowCLITokenModal] = useState(false);
  const [showRevokeCLITokenModal, setShowRevokeCLITokenModal] = useState(false);
  const [cliTokenToRevoke, setCliTokenToRevoke] = useState<{ id: string; name: string } | null>(null);
  const [newCLIToken, setNewCLIToken] = useState<{ id: string; token: string; name: string } | null>(null);
  const [cliTokenName, setCliTokenName] = useState('');
  const [cliTokenExpiresAt, setCliTokenExpiresAt] = useState('');
  const [isCreatingCLIToken, setIsCreatingCLIToken] = useState(false);

  // Check view mode from localStorage and listen for changes
  const [viewMode, setViewMode] = useState<'user' | 'admin' | 'sysadmin'>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as 'user' | 'admin' | 'sysadmin';
        if (user?.role === 'sysadmin') {
          return mode;
        } else if (user?.role === 'admin') {
          return mode === 'sysadmin' ? 'admin' : mode;
        }
        return 'user';
      }
      if (user?.role === 'admin' || user?.role === 'sysadmin') {
        return 'admin';
      }
      return 'user';
    } catch {
      return 'user';
    }
  });

  // Listen for view mode changes (only view-mode-changed so sysadmin is not overwritten by legacy administration-view-changed)
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.type !== 'view-mode-changed') return;
      const newMode = event.detail?.viewMode;
      if (newMode) {
        setViewMode(newMode as 'user' | 'admin' | 'sysadmin');
      }
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
    };
  }, []);
  
  const effectiveMode = user?.role === 'sysadmin' 
    ? viewMode 
    : (user?.role === 'admin' ? (viewMode === 'sysadmin' ? 'admin' : viewMode) : 'user');
  
  // Invalidate queries when mode changes to ensure data updates immediately
  useEffect(() => {
    // Invalidate device queries when effectiveMode changes
    queryClient.invalidateQueries({ 
      queryKey: ['devices'],
      exact: false // Invalidate all queries that start with 'devices'
    });
    queryClient.invalidateQueries({ 
      queryKey: ['users-for-devices'],
      exact: false // Invalidate all queries that start with 'users-for-devices'
    });
  }, [effectiveMode, queryClient]);
  
  // Device settings for admins
  const { settings: deviceSettings, loading: deviceSettingsLoading } = useCompanyDeviceSettings();
  const [allowRevokeDevice, setAllowRevokeDevice] = useState(true);
  const [allowCreateCLIToken, setAllowCreateCLIToken] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const settingsRef = useRef({ allowRevokeDevice: true, allowCreateCLIToken: true });

  // Load device settings when available
  useEffect(() => {
    if (deviceSettings) {
      setAllowRevokeDevice(deviceSettings.allow_revoke_device);
      setAllowCreateCLIToken(deviceSettings.allow_create_cli_token);
      settingsRef.current = {
        allowRevokeDevice: deviceSettings.allow_revoke_device,
        allowCreateCLIToken: deviceSettings.allow_create_cli_token,
      };
    }
  }, [deviceSettings]);

  // Handle toggle changes
  const handleToggle = async (setting: 'revoke' | 'cli', value: boolean) => {
    if (!isAdmin || !user?.company_id) return;

    const newSettings = {
      allow_revoke_device: setting === 'revoke' ? value : allowRevokeDevice,
      allow_create_cli_token: setting === 'cli' ? value : allowCreateCLIToken,
    };

    // Optimistically update UI
    if (setting === 'revoke') {
      setAllowRevokeDevice(value);
    } else {
      setAllowCreateCLIToken(value);
    }

    try {
      setIsSaving(true);
      await apiService.updateCompanyDeviceSettings(user.company_id, newSettings);
      settingsRef.current = {
        allowRevokeDevice: newSettings.allow_revoke_device,
        allowCreateCLIToken: newSettings.allow_create_cli_token,
      };
      toast.success('Device settings updated successfully');
    } catch (err: any) {
      // Revert on error
      if (setting === 'revoke') {
        setAllowRevokeDevice(settingsRef.current.allowRevokeDevice);
      } else {
        setAllowCreateCLIToken(settingsRef.current.allowCreateCLIToken);
      }
      toast.error(err.response?.data?.error || 'Failed to update device settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Whether we're showing the current user's own devices (flat list) — CLI section only in this case
  const showOwnDevices = isRegularUser || (isAdmin && effectiveMode === 'user');

  // Fetch devices based on user role and view mode
  const { data: allDevices = [], isLoading: devicesLoading, error } = useQuery<Device[]>({
    queryKey: ['devices', user?.id, user?.company_id, effectiveMode],
    queryFn: async () => {
      // User/Standard mode: always show only own devices (for all users including sysadmins)
      if (effectiveMode === 'user') {
        const response = await apiService.getDevices();
        return response || [];
      }
      
      // Regular users always get their own devices (fallback)
      if (isRegularUser) {
        const response = await apiService.getDevices();
        return response || [];
      }
      
      // Admins: check view mode
      if (isAdmin) {
        // Admin mode: show company devices
        if (effectiveMode === 'admin' && user?.company_id) {
          // For sysadmins in admin mode, use company endpoint; for regular admins, same endpoint
          const response = await apiService.get(`/devices/company/${user.company_id}`);
          return response.data || [];
        }
        
        // SysAdmin mode: show all devices system-wide
        if (effectiveMode === 'sysadmin' && isSysadmin) {
          const response = await apiService.get('/devices/all');
          return response.data.devices || [];
        }
      }
      
      return [];
    },
    enabled: !!user,
  });

  // Fetch users to get names/emails (only needed for admin view with grouped devices)
  const { data: usersData } = useQuery({
    queryKey: ['users-for-devices', user?.company_id, effectiveMode],
    queryFn: async () => {
      // In admin mode, only fetch users from the company (even for sysadmins)
      if (effectiveMode === 'admin' && user?.company_id) {
        const response = await apiService.getUsers({ company_id: user.company_id });
        return response.users || [];
      }
      // In sysadmin mode, fetch all users
      if (effectiveMode === 'sysadmin' && isSysadmin) {
        const response = await apiService.getUsers();
        return response.users || [];
      }
      return [];
    },
    enabled: (effectiveMode === 'sysadmin' || effectiveMode === 'admin') && !!user,
  });

  // CLI tokens (only when viewing own devices)
  const { data: cliTokens = [], refetch: refetchCLITokens } = useQuery({
    queryKey: ['cli-tokens', user?.id],
    queryFn: () => apiService.listCLITokens(),
    enabled: showOwnDevices && !!user,
  });

  // CLI add-on gate: the company must have CLI enabled and not past the add-on's
  // own expiry. Controls below are hidden when it's off (the backend 403s too).
  const { data: companySubData } = useQuery({
    queryKey: ['company-cli-subscription', user?.company_id],
    queryFn: () => apiService.getSubscriptionByCompany(user!.company_id as string),
    enabled: !!user?.company_id,
  });
  const cliAddonEnabled = (() => {
    const s = companySubData?.subscription as Record<string, unknown> | undefined;
    if (!s) return isSysadmin; // sysadmin without a company context: don't hide
    if (!s.cli_enabled) return false;
    const until = s.cli_licensed_until as string | undefined;
    if (until && new Date(until) <= new Date()) return false;
    return true;
  })();

  const nonWebDeviceCount = allDevices.filter((d) => d.type !== 'web').length;
  const canCreateCLIDevice =
    cliAddonEnabled && nonWebDeviceCount < 3 && (isAdmin || deviceSettings?.allow_create_cli_token !== false);

  const handleCreateCLIToken = async () => {
    if (!cliTokenName.trim()) {
      toast.error(t('toast.tokenNameRequired'));
      return;
    }
    const success = await createCLITokenChallenge.executeWithChallenge(async () => {
      setIsCreatingCLIToken(true);
      try {
        const expiresAt = cliTokenExpiresAt ? new Date(cliTokenExpiresAt).toISOString() : undefined;
        const result = await apiService.createCLIToken(cliTokenName.trim(), expiresAt);
        setNewCLIToken({ id: result.id, token: result.token, name: result.name });
        setShowCreateCLITokenModal(false);
        setShowCLITokenModal(true);
        setCliTokenName('');
        setCliTokenExpiresAt('');
        await refetchCLITokens();
        queryClient.invalidateQueries({ queryKey: ['devices'] });
        queryClient.invalidateQueries({ queryKey: ['devices-count'] });
        toast.success(t('toast.cliTokenCreated'));
      } catch (err: any) {
        toast.error(err.response?.data?.error || t('toast.cliTokenCreateFailed'));
        throw err;
      } finally {
        setIsCreatingCLIToken(false);
      }
    });
    if (!success) return;
  };

  const handleRevokeCLITokenClick = (tokenId: string, tokenName: string) => {
    setCliTokenToRevoke({ id: tokenId, name: tokenName });
    setShowRevokeCLITokenModal(true);
  };

  const confirmRevokeCLIToken = async () => {
    if (!cliTokenToRevoke) return;
    try {
      await apiService.revokeCLIToken(cliTokenToRevoke.id);
      await refetchCLITokens();
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['devices-count'] });
      setShowRevokeCLITokenModal(false);
      setCliTokenToRevoke(null);
      toast.success(t('toast.cliTokenRevoked'));
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('toast.cliTokenRevokeFailed'));
    }
  };

  const cancelRevokeCLIToken = () => {
    setShowRevokeCLITokenModal(false);
    setCliTokenToRevoke(null);
  };

  const formatCLIDate = (dateString: string) => {
    const d = new Date(dateString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day} ${h}:${min}:${s}`;
  };

  // Group devices by user (only for admin view, regular users see flat list)
  const devicesByUser = useMemo(() => {
    const grouped: Record<string, DeviceWithUser[]> = {};
    
    // For regular users or admin in user mode, don't group
    if (isRegularUser || (isAdmin && effectiveMode === 'user')) {
      return {};
    }
    
    // Debug: Log usersData for troubleshooting
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 DEVICES: Grouping devices', {
        deviceCount: allDevices.length,
        usersDataCount: usersData?.length || 0,
        usersDataIds: usersData?.map((u: any) => u.id) || [],
      });
    }
    
    allDevices.forEach((device) => {
      // Get user IDs from device (populated by backend)
      // Backend returns UUIDs, convert to strings
      const userIds = (device.user_ids || []).map((id: any) => 
        typeof id === 'string' ? id : id.toString()
      );
      
      if (userIds.length === 0) {
        // Device with no users - add to "Unknown" group
        const unknownKey = 'unknown';
        if (!grouped[unknownKey]) {
          grouped[unknownKey] = [];
        }
        grouped[unknownKey].push({
          device,
          userId: unknownKey,
          userName: 'Unknown',
          userEmail: undefined,
        });
      } else {
        // Track if device was added to any known user's group
        let addedToKnownUser = false;
        
        // First, try to add device to known users' groups
        userIds.forEach((userId: string) => {
          const user = usersData?.find((u: any) => u.id === userId);
          if (user) {
            // User exists in usersData - add to their group
            if (!grouped[userId]) {
              grouped[userId] = [];
            }
            grouped[userId].push({
              device,
              userId,
              userName: user.name,
              userEmail: user.email,
            });
            addedToKnownUser = true;
          }
        });
        
        // If device wasn't added to any known user's group, add to "Unknown User"
        // This handles cases where all user_ids don't exist in usersData
        if (!addedToKnownUser) {
          const unknownKey = 'unknown';
          if (!grouped[unknownKey]) {
            grouped[unknownKey] = [];
          }
          grouped[unknownKey].push({
            device,
            userId: unknownKey,
            userName: 'Unknown',
            userEmail: undefined,
          });
          
          // Debug: Log when device goes to Unknown User
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ DEVICES: Device added to Unknown User', {
              deviceId: device.id,
              deviceName: device.name,
              userIds: userIds,
              reason: 'No matching users found in usersData',
            });
          }
        }
      }
    });
    
    return grouped;
  }, [allDevices, usersData, isRegularUser, isAdmin, effectiveMode]);

  // Toggle accordion
  const toggleUser = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Get device type icon
  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'mobile':
        return DevicePhoneMobileIcon;
      case 'desktop':
      case 'server':
      case 'cli':
        return ComputerDesktopIcon;
      default:
        return ComputerDesktopIcon;
    }
  };

  // Handle delete click - open modal
  const handleDeleteClick = (device: Device) => {
    setDeviceToDelete(device);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deviceToDelete) return;

    setDeletingDeviceId(deviceToDelete.id);
    try {
      await apiService.deleteDevice(deviceToDelete.id);
      toast.success('Device deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['devices-count'] });
      setShowDeleteModal(false);
      setDeviceToDelete(null);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete device');
    } finally {
      setDeletingDeviceId(null);
    }
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('failedToLoad')}</p>
      </div>
    );
  }

  const userIds = Object.keys(devicesByUser).sort((a, b) => {
    // Sort "unknown" to the end
    if (a === 'unknown') return 1;
    if (b === 'unknown') return -1;
    // Sort by user name
    const userA = usersData?.find((u: any) => u.id === a);
    const userB = usersData?.find((u: any) => u.id === b);
    const nameA = userA?.name || userA?.email || '';
    const nameB = userB?.name || userB?.email || '';
    return nameA.localeCompare(nameB);
  });

  const isLoading = devicesLoading;

  return (
    <div className="space-y-6">
      {/* Device Settings Toggles - Only for admins in admin mode (not sysadmin mode) */}
      {isAdmin && effectiveMode === 'admin' && user?.company_id && !deviceSettingsLoading && (
        <div className="card p-6 space-y-4">
  
          <div className="space-y-4  border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('settings.allowRevokeDevices')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('settings.allowRevokeDevicesDesc')}
                </p>
              </div>
              <ToggleSwitch
                checked={allowRevokeDevice}
                onChange={(checked) => handleToggle('revoke', checked)}
                disabled={isSaving}
              />
            </div>
            {cliAddonEnabled && (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('settings.allowCreateCLITokens')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('settings.allowCreateCLITokensDesc')}
                  </p>
                </div>
                <ToggleSwitch
                  checked={allowCreateCLIToken}
                  onChange={(checked) => handleToggle('cli', checked)}
                  disabled={isSaving}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* CLI Activation Tokens — only when viewing own devices AND the company's CLI add-on is enabled */}
      {showOwnDevices && cliAddonEnabled && (
        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t('cli.availableCLITokens')}
            </h3>
            {canCreateCLIDevice && (
              <button
                onClick={() => setShowCreateCLITokenModal(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-governate-600 hover:bg-governate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                {t('cli.createCLIToken')}
              </button>
            )}
          </div>
          {!canCreateCLIDevice && nonWebDeviceCount >= 3 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('cli.deviceLimitReached')}
            </p>
          )}
          {cliTokens.length > 0 && (
            <div className="space-y-3">
              {cliTokens.map((token: { id: string; name: string; status: string; expires_at?: string; created_at: string; last_used_at?: string }) => (
                <div
                  key={token.id}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <CommandLineIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{token.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('cli.status')}: {token.status} • {t('cli.created')} {formatCLIDate(token.created_at)}
                        {token.last_used_at && ` • ${t('cli.lastUsed')} ${formatCLIDate(token.last_used_at)}`}
                        {token.expires_at && ` • ${t('cli.expires')} ${formatCLIDate(token.expires_at)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {token.status === 'active' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {t('cli.active')}
                      </span>
                    )}
                    {token.status === 'revoked' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        {t('cli.revoked')}
                      </span>
                    )}
                    {token.status === 'active' && (
                      <button
                        onClick={() => handleRevokeCLITokenClick(token.id, token.name)}
                        className="text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        {t('cli.revoke')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : (isRegularUser || (isAdmin && effectiveMode === 'user')) ? (
        // Flat list for regular users or admins in standard view
        allDevices.length === 0 ? (
          <div className="card text-center py-12">
            <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('empty.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('empty.description')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allDevices.map((device) => {
              const DeviceIcon = getDeviceIcon(device.type);
              // For regular users, check company setting. For admins, always allow (subject to backend checks)
              const canRevoke = isAdmin || (deviceSettings?.allow_revoke_device !== false && device.status !== 'revoked');
              
              return (
                <div
                  key={device.id}
                  className="card p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <DeviceIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {device.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {device.type} • {t('created')} {formatDate(device.created_at)}
                          {device.last_seen && ` • ${t('lastSeen')} ${formatDate(device.last_seen)}`}
                        </p>
                      </div>
                      {device.status && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                            device.status === 'active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {device.status}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        onClick={() => navigate(`/devices/${device.id}`)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                        title={t('buttons.viewDeviceDetails')}
                      >
                        <InformationCircleIcon className="w-4 h-4 mr-1" />
                        {t('buttons.details')}
                      </button>
                      {canRevoke && (
                        <button
                          onClick={() => handleDeleteClick(device)}
                          disabled={deletingDeviceId === device.id}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('buttons.revokeDevice')}
                        >
                          <TrashIcon className="w-4 h-4 mr-1" />
                          {t('buttons.revokeDevice')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : userIds.length === 0 ? (
        <div className="card text-center py-12">
          <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('empty.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('empty.description')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {userIds.map(userId => {
            const userDevices = devicesByUser[userId];
            const isExpanded = expandedUsers.has(userId);
            const firstDevice = userDevices[0];
            const displayName = userId === 'unknown' 
              ? t('unknownUser') 
              : (firstDevice.userName || firstDevice.userEmail || t('unknownUser'));
            const displayEmail = userId === 'unknown' ? undefined : firstDevice.userEmail;

            return (
              <div key={userId} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
                {/* Accordion Header */}
                <div className="w-full flex items-center justify-between px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <button
                    onClick={() => toggleUser(userId)}
                    className="flex-1 flex items-center space-x-2 text-left"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                    )}
                    <UserIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <div className="text-left">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {displayName}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {displayEmail && `${displayEmail} • `}
                        {t(userDevices.length === 1 ? 'count.device' : 'count.devices', { count: userDevices.length })}
                      </p>
                    </div>
                  </button>
                  {!isExpanded && userDevices.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/devices/${userDevices[0].device.id}`);
                      }}
                      className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                      title="View device details"
                    >
                      <InformationCircleIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Accordion Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {userDevices.map(({ device }) => {
                        const DeviceIcon = getDeviceIcon(device.type);
                        // For regular users, check company setting. For admins, always allow (subject to backend checks)
                        const canRevoke = isAdmin || (deviceSettings?.allow_revoke_device !== false && device.status !== 'revoked');
                        
                        return (
                          <div
                            key={device.id}
                            className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <DeviceIcon className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {device.name}
                                </h4>
                                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize flex-shrink-0">
                                  {device.type}
                                </span>
                                {device.status && (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                                      device.status === 'active'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                    }`}
                                  >
                                    {device.status}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                <span>{t('created')} {formatDate(device.created_at)}</span>
                                {device.last_seen && (
                                  <span>{t('lastSeen')} {formatDate(device.last_seen)}</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                <button
                                  onClick={() => navigate(`/devices/${device.id}`)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                                  title={t('buttons.viewDeviceDetails')}
                                >
                                  <InformationCircleIcon className="w-3.5 h-3.5" />
                                </button>
                                {canRevoke && (
                                  <button
                                    onClick={() => handleDeleteClick(device)}
                                    disabled={deletingDeviceId === device.id}
                                    className="inline-flex items-center p-1.5 border border-transparent rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={t('buttons.deleteDevice')}
                                  >
                                    <TrashIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deviceToDelete && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !deletingDeviceId && setShowDeleteModal(false)}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      Delete Device
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete <strong>{deviceToDelete.name}</strong>? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deletingDeviceId === deviceToDelete.id}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {deletingDeviceId === deviceToDelete.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeviceToDelete(null);
                  }}
                  disabled={deletingDeviceId === deviceToDelete.id}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create CLI Token Modal */}
      {showCreateCLITokenModal && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75" onClick={() => !isCreatingCLIToken && setShowCreateCLITokenModal(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('modals.createCLIToken.title')}</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="cli-token-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('modals.createCLIToken.tokenName')}
                  </label>
                  <input
                    type="text"
                    id="cli-token-name"
                    value={cliTokenName}
                    onChange={(e) => setCliTokenName(e.target.value)}
                    placeholder={t('modals.createCLIToken.tokenNamePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-governate-500"
                  />
                </div>
                <div>
                  <label htmlFor="cli-token-expires" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('modals.createCLIToken.expiresAtOptional')}
                  </label>
                  <input
                    type="datetime-local"
                    id="cli-token-expires"
                    value={cliTokenExpiresAt}
                    onChange={(e) => setCliTokenExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-governate-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('modals.createCLIToken.leaveEmptyNoExpiration')}</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => { setShowCreateCLITokenModal(false); setCliTokenName(''); setCliTokenExpiresAt(''); }}
                  disabled={isCreatingCLIToken}
                  className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 disabled:opacity-50"
                >
                  {t('modals.createCLIToken.cancel')}
                </button>
                <button
                  onClick={handleCreateCLIToken}
                  disabled={isCreatingCLIToken || !cliTokenName.trim()}
                  className="px-4 py-2 bg-governate-600 text-white text-sm font-medium rounded-md hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingCLIToken ? t('modals.createCLIToken.creating') : t('modals.createCLIToken.createToken')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CLI Token Display Modal (copy token) */}
      {showCLITokenModal && newCLIToken && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75" onClick={() => { setShowCLITokenModal(false); setNewCLIToken(null); }} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mx-auto mb-4">
                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 text-center">{t('modals.cliTokenCreated.title')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center">{t('modals.cliTokenCreated.saveSecurely')}</p>
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-md mb-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('modals.cliTokenCreated.tokenNameLabel')}</label>
                <p className="text-sm font-mono text-gray-900 dark:text-gray-100">{newCLIToken.name}</p>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-md mb-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('modals.cliTokenCreated.activationToken')}</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono break-all bg-white dark:bg-gray-800 p-2 rounded border text-gray-900 dark:text-gray-100">{newCLIToken.token}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(newCLIToken.token); toast.success(t('toast.tokenCopied')); }}
                    className="px-3 py-2 text-sm font-medium text-governate-600 hover:text-governate-700 shrink-0"
                  >
                    {t('modals.cliTokenCreated.copy')}
                  </button>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded-md mb-4">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>{t('modals.cliTokenCreated.usage')}</strong> <code className="bg-white dark:bg-gray-800 px-1 rounded">governate activate --token {newCLIToken.token.substring(0, 20)}...</code> {t('modals.cliTokenCreated.usageOnCli')}
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => { setShowCLITokenModal(false); setNewCLIToken(null); }}
                  className="px-4 py-2 bg-governate-600 text-white text-sm font-medium rounded-md hover:bg-governate-700"
                >
                  {t('modals.cliTokenCreated.iveSavedIt')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Revoke CLI Token Modal */}
      {showRevokeCLITokenModal && cliTokenToRevoke && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75" onClick={cancelRevokeCLIToken} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 text-center">
              <div className="flex justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mx-auto mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('modals.revokeCLIToken.title')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t('modals.revokeCLIToken.descriptionBefore')}<strong>{cliTokenToRevoke.name}</strong>{t('modals.revokeCLIToken.descriptionAfter')}
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={cancelRevokeCLIToken} className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600">
                  {t('modals.revokeCLIToken.cancel')}
                </button>
                <button onClick={confirmRevokeCLIToken} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700">
                  {t('modals.revokeCLIToken.revoke')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Devices;

